/**
 * AustrittModal — Erfasst/bearbeitet austritt_datum + aufloesungsart + begruendung
 * atomar in einem einzigen PUT /api/mitarbeiter/:id/stammdaten.
 *
 * Fix für Henne-Ei-Problem aus #896: EditableField schickt pro Klick nur ein
 * Feld — Backend-Cross-Field-Validator verlangt aber alle 3 atomar.
 *
 * @since v2.395.2 (#906)
 */
import { useState, useEffect, useRef } from 'react';
import { X, LogOut, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import ConfirmDialog from './ConfirmDialog';

const AUFLOESUNGSART_OPTIONS = [
  'kuendigung_an', 'kuendigung_ag',
  'einvernehmlich_an', 'einvernehmlich_ag',
  'probezeit_an', 'probezeit_ag',
  'entlassung', 'vorzeitig', 'zeitablauf',
  'pensionierung', 'tod',
];

export default function AustrittModal({ ma, mode, onClose, onSaved }) {
  const { t } = useLanguage();
  const isEdit = mode === 'edit';
  const today = new Date().toISOString().slice(0, 10);

  const [datum, setDatum] = useState(
    isEdit && ma?.austritt_datum ? String(ma.austritt_datum).slice(0, 10) : today
  );
  const [art, setArt] = useState(
    isEdit && ma?.austritt_aufloesungsart ? ma.austritt_aufloesungsart : ''
  );
  const [begruendung, setBegruendung] = useState(
    isEdit && ma?.austritt_begruendung ? ma.austritt_begruendung : ''
  );
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const datumRef = useRef(null);

  useEffect(() => {
    datumRef.current?.focus();
    const handleKey = (e) => { if (e.key === 'Escape' && !confirmRemove) onClose?.(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, confirmRemove]);

  const begTrimLen = String(begruendung || '').trim().length;

  function validate() {
    const errs = {};
    if (!datum) errs.datum = t('austritt.modal.datum_required', 'Austrittsdatum ist Pflicht');
    if (!art) errs.art = t('austritt.modal.aufloesungsart_required', 'Auflösungsart wählen');
    if (begTrimLen === 0) errs.begruendung = t('austritt.modal.begruendung_required', 'Begründung ist Pflicht');
    else if (begTrimLen < 10) errs.begruendung = t('austritt.modal.begruendung_too_short', 'Mindestens 10 Zeichen');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function save() {
    if (!validate() || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          austritt_datum: datum,
          austritt_aufloesungsart: art,
          austritt_begruendung: begruendung.trim(),
        }),
      });
      const d = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
      if (!res.ok || d.error) {
        setSaveError(d.error || `HTTP ${res.status}`);
        setSaving(false);
        return;
      }
      onSaved?.(d, 'saved');
    } catch (err) {
      setSaveError(String(err?.message || err));
      setSaving(false);
    }
  }

  async function remove() {
    setConfirmRemove(false);
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ austritt_datum: null }),
      });
      const d = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
      if (!res.ok || d.error) {
        setSaveError(d.error || `HTTP ${res.status}`);
        setSaving(false);
        return;
      }
      onSaved?.(d, 'removed');
    } catch (err) {
      setSaveError(String(err?.message || err));
      setSaving(false);
    }
  }

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
        onClick={onClose}
      >
        <div
          className="bg-surface-card rounded-card border border-line w-full max-w-[500px] mx-4"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-line">
            <div className="flex items-center gap-2">
              <LogOut size={18} className="text-[var(--text-secondary)]" />
              <h2 className="text-base font-semibold text-[var(--text-primary)]">
                {isEdit
                  ? t('austritt.modal.title_edit', 'Austritt bearbeiten')
                  : t('austritt.modal.title_create', 'Austritt erfassen')}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {(ma?.vorname || ma?.nachname) && (
              <div className="text-xs text-[var(--text-tertiary)]">
                {ma.vorname} {ma.nachname}
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                {t('austritt.modal.datum_label', 'Austrittsdatum')} *
              </label>
              <input
                ref={datumRef}
                type="date"
                value={datum || ''}
                onChange={e => setDatum(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-line bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
              />
              {errors.datum && (
                <div className="text-xs text-rose-600 mt-1">{errors.datum}</div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                {t('austritt.aufloesungsart_label', 'Auflösungsart')} *
              </label>
              <select
                value={art}
                onChange={e => setArt(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-line bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
              >
                <option value="">—</option>
                {AUFLOESUNGSART_OPTIONS.map(key => (
                  <option key={key} value={key}>
                    {t(`austritt.aufloesungsart.${key}`, key)}
                  </option>
                ))}
              </select>
              {errors.art && (
                <div className="text-xs text-rose-600 mt-1">{errors.art}</div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-[var(--text-secondary)]">
                  {t('austritt.begruendung_label', 'Begründung zum Austritt')} *
                </label>
                <span
                  className={`text-[10px] font-mono ${
                    begTrimLen >= 10 ? 'text-[var(--text-tertiary)]' : 'text-rose-600'
                  }`}
                >
                  {t('austritt.modal.begruendung_counter', '{{n}}/10').replace('{{n}}', begTrimLen)}
                </span>
              </div>
              <textarea
                value={begruendung}
                onChange={e => setBegruendung(e.target.value)}
                rows={4}
                placeholder={t(
                  'austritt.begruendung_placeholder',
                  'z.B. Wechsel in Vollzeit-Position, persönliche Gründe, …'
                )}
                className="w-full px-3 py-2 text-sm rounded-lg border border-line bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
              />
              {errors.begruendung && (
                <div className="text-xs text-rose-600 mt-1">{errors.begruendung}</div>
              )}
            </div>

            {saveError && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg border border-rose-200 bg-rose-50 text-xs text-rose-700">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">
                    {t('austritt.modal.save_error', 'Speichern fehlgeschlagen')}
                  </div>
                  <div>{saveError}</div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-line">
            {isEdit ? (
              <button
                type="button"
                disabled={saving}
                onClick={() => setConfirmRemove(true)}
                className="px-3 py-2 text-xs font-medium rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-50"
              >
                {t('austritt.modal.remove_button', 'Austritt entfernen')}
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-lg border border-line text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] disabled:opacity-50"
              >
                {t('austritt.modal.cancel', 'Abbrechen')}
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50"
                style={{ backgroundColor: '#338edc' }}
              >
                {saving ? '…' : t('austritt.modal.save', 'Speichern')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRemove}
        variant="danger"
        title={t('austritt.modal.remove_confirm_title', 'Austritt entfernen?')}
        message={t('austritt.modal.remove_confirm_msg', 'Alle Austrittsdaten werden gelöscht.')}
        confirmLabel={t('austritt.modal.remove_button', 'Austritt entfernen')}
        cancelLabel={t('austritt.modal.cancel', 'Abbrechen')}
        onConfirm={remove}
        onCancel={() => setConfirmRemove(false)}
      />
    </>
  );
}
